
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useHigherEdOrgManagerQuery, useUserProfileQuery } from '@uplift/queries';
import { EditableSectionContainer, EditableSectionRow } from '@uplift/ui-v2';

import { ClickToCopyWrapper } from '../../profile';
import { ManagerNameForm, useManagerNameForm } from './forms/ManagerNameForm';
import { ManagerTitleForm, useManagerTitleForm } from './forms/ManagerTitleForm';

type ManagerProfileSectionProps = {
  managerId: string;
  isCareApp?: boolean;
};

export const ManagerProfileSection: React.VFC<ManagerProfileSectionProps> = ({ managerId, isCareApp }) => {
  const { classes } = useManagerProfileSectionStyles();

  const { data: manager } = useHigherEdOrgManagerQuery(managerId, { enabled: !!managerId, suspense: true });
  const { data: profile } = useUserProfileQuery(managerId, { enabled: !!managerId, suspense: true });

  const suffixes = profile?.suffixes?.join(', ');
  const managerName = `${profile?.prefix ?? ''} ${manager?.user?.firstName} ${manager?.user?.lastName}${suffixes && `, ${suffixes}`}`;
  const { managerNameFormik } = useManagerNameForm(manager!);
  const { managerTitleFormik } = useManagerTitleForm(manager!, profile?.title);

  return (
    <EditableSectionContainer
      title="Profile"
      rowDividers
      formikConfigs={{
        'name': managerNameFormik,
        'title': managerTitleFormik,
      }}
    >
      <EditableSectionRow
        label="Name"
        rowKey="name"
        form={<ManagerNameForm />}
        kind="editable-row"
        testId="manager-name-editable-row"
      >
        <Box display="flex">
          <Box flex={1}>
            <Typography color="textSecondary">Full Name</Typography>
            <Typography style={{ fontWeight: 500 }}>
              {managerName}
            </Typography>
          </Box>
        </Box>
      </EditableSectionRow>
      <EditableSectionRow
        label="Job Title"
        rowKey="title"
        form={<ManagerTitleForm />}
        kind="editable-row"
        testId="manager-title-editable-row"
      >
        <Typography className={classes.title}>
          {profile?.title || ''}
        </Typography>
      </EditableSectionRow>

      {!isCareApp ? (
        <EditableSectionRow
          label="Account email"
          rowKey="email"
          kind="custom"
          testId="manager-email-row"
        >
          <Box className={classes.email}><ClickToCopyWrapper text={manager?.user?.email ?? ''} /></Box>
        </EditableSectionRow>
      ) : null}
    </EditableSectionContainer>
  );
};

const useManagerProfileSectionStyles = makeStyles({ name: 'ManagerProfileSection' })(({
  title: {
    fontWeight: 500,
    textTransform: 'capitalize',
    letterSpacing: '0.4px'
  },

  email: {
    fontWeight: 500,
    letterSpacing: '0.4px'
  }
}));
